// Deliberately first, and above the Phaser import: it swaps localStorage for an
// in-memory copy when this is a playtest launch, and half the modules below read
// persistence at import time. See playtestStorage.ts.
import './playtestStorage';
// Second, and above everything that can throw: it installs the window-level
// error/unhandledrejection handlers, so a crash during boot -- before the UI,
// the scene or the canvas exist -- is still reported rather than dying in a
// console nobody is reading. It sits below playtestStorage for the same reason
// everything else does: it reaches analytics.ts, which reads persistence at
// import time.
import { initErrorTelemetry, reportError } from './errorTelemetry';
import Phaser from 'phaser';
import { GameScene } from './scenes/GameScene';
import { initUI } from './ui';
import { awaitPlaytest } from './playtest';
import { initClockMode } from './clockMode';

initErrorTelemetry();

// An async function rather than a top-level await: the build targets es2020 and
// Safari 14 (this also ships through Capacitor), neither of which has top-level
// await, and raising the target to buy syntax for one call would quietly narrow
// which devices the game runs on.
async function boot(): Promise<void> {
  // Before the UI and the scene exist, so the first frame drawn is already the
  // playtested world rather than room0. A no-op in ordinary play.
  await awaitPlaytest();

  // After the playtest handshake and before the scene: the payload carries a
  // whole PhysicsConfig, clock included, and a room authored to be verified on
  // the turn clock has to be verified on it whatever this browser's menu
  // remembers -- so a playtest keeps its payload's clock and only an ordinary
  // session takes the setting's (see clockMode.ts). Before the scene because
  // the scene's very first frame asks which clock it is on.
  initClockMode();

  initUI();

  new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'app',
    backgroundColor: '#0d0f12',
    pixelArt: true,
    scale: {
      // RESIZE fills whatever space the parent has, at its actual aspect ratio -- a
      // fixed 800x600 (4:3, landscape-shaped) canvas with FIT mode letterboxes down to
      // a thin strip in the middle of a tall phone screen, with large black bars above
      // and below eating most of the view. That's what looked "blank" on a phone: the
      // game was rendering, just squeezed into a sliver most people wouldn't notice.
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.NO_CENTER,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    scene: [GameScene],
  });
}

// A boot failure is the one crash with no recoverable state and no HUD to
// degrade into -- there is no canvas and no #cast-result-hud to write to yet,
// so this writes its own bare node. `reportError` is still worth calling
// first: the window handler would catch the rejection anyway, but only as an
// 'unhandled-rejection' with no indication that it was boot specifically that
// failed, which is the single most useful thing to know about it.
void boot().catch((err) => {
  reportError(err, 'boot');
  const el = document.getElementById('app');
  if (el) el.innerHTML = '<p style="color:#c9c4bb;font:14px system-ui;padding:24px">The game failed to start. Reloading the page usually fixes this.</p>';
});
